import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Stethoscope, Upload, Check, AlertTriangle } from "lucide-react";
import spApi, { spErrorMessage, setSpToken } from "@/lib/spApi";

function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

const input = "w-full bg-[#111111] border border-[#333333] rounded-xl px-4 py-3.5 min-h-[44px] text-[#F8FAFC] placeholder:text-[#666666] focus:outline-none focus:border-[#EF9F27]";
const label = "block text-sm font-medium text-[#A1A1AA] mb-1.5 mt-3";

export default function SpecialistSignup() {
  const navigate = useNavigate();
  const licenceRef = useRef(null);
  const idRef = useRef(null);
  const [step, setStep] = useState(0);
  const [meta, setMeta] = useState({ specialties: [], languages: [] });
  const [submitting, setSubmitting] = useState(false);
  const [licenceFile, setLicenceFile] = useState(null);
  const [idFile, setIdFile] = useState(null);
  const [f, setF] = useState({
    full_name: "", email: "", password: "", country: "", specialty: "", specialty_other: "",
    languages: [], licence_number: "", licence_body: "", licence_issue_country: "",
    licence_issue_date: "", licence_no_expiry: false, licence_expiry_date: "",
    credentials_summary: "", review_fee: "", currency: "USD", avg_turnaround_days: 3,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    spApi.get("/specialists/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const expiryPast = f.licence_expiry_date && f.licence_expiry_date < today;

  const step0ok = f.full_name && /.+@.+\..+/.test(f.email) && f.password.length >= 6 && f.country && f.specialty && (f.specialty !== "Other" || f.specialty_other);
  const step1ok = f.licence_number && f.licence_body && f.licence_issue_country && f.licence_issue_date && (f.licence_no_expiry || f.licence_expiry_date) && licenceFile;

  const toggleLang = (l) => set("languages", f.languages.includes(l) ? f.languages.filter((x) => x !== l) : [...f.languages, l]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await spApi.post("/specialists/signup", {
        ...f,
        review_fee: Number(f.review_fee) || 0,
        avg_turnaround_days: Number(f.avg_turnaround_days) || 3,
        licence_expiry_date: f.licence_no_expiry ? null : f.licence_expiry_date,
      });
      setSpToken(data.token);
      // upload docs
      const lic = await fileToDataUrl(licenceFile);
      await spApi.post("/specialists/documents", { document_type: "licence_certificate", data_url: lic, label: licenceFile.name });
      if (idFile) {
        const idUrl = await fileToDataUrl(idFile);
        await spApi.post("/specialists/documents", { document_type: "photo_id", data_url: idUrl, label: idFile.name });
      }
      toast.success("Application submitted for review.");
      navigate("/specialist/status", { replace: true });
    } catch (e) {
      toast.error(spErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="care-scope min-h-screen w-full flex justify-center bg-black">
      <div className="relative w-full max-w-[430px] min-h-screen bg-[#111111] flex flex-col">
        <header className="px-6 pt-8 pb-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#EF9F27] text-[#111111] mb-4">
            <Stethoscope size={24} />
          </div>
          <h1 className="font-head text-2xl font-extrabold text-[#F8FAFC]">Join as a specialist</h1>
          <p className="text-sm text-[#A1A1AA] mt-1">Apply to provide second opinions. Your details are verified before you go live.</p>
          <div className="flex gap-2 mt-4">
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i <= step ? "bg-[#EF9F27] w-8" : "bg-[#333333] w-4"}`} />
            ))}
          </div>
        </header>

        <div className="flex-1 px-6 pb-8">
          {step === 0 && (
            <div data-testid="sp-step-basic">
              <h2 className="font-head text-lg font-bold text-[#F8FAFC]">Basic details</h2>
              <label className={label}>Full name</label>
              <input data-testid="sp-fullname" className={input} value={f.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Dr Jane Doe" />
              <label className={label}>Email</label>
              <input data-testid="sp-email" className={input} type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />
              <label className={label}>Password</label>
              <input data-testid="sp-password" className={input} type="password" value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="At least 6 characters" />
              <label className={label}>Country of residence</label>
              <input data-testid="sp-country" className={input} value={f.country} onChange={(e) => set("country", e.target.value)} placeholder="e.g. United Kingdom" />
              <label className={label}>Specialty</label>
              <select data-testid="sp-specialty" className={input} value={f.specialty} onChange={(e) => set("specialty", e.target.value)}>
                <option value="">Choose a specialty</option>
                {meta.specialties.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {f.specialty === "Other" && (
                <input data-testid="sp-specialty-other" className={`${input} mt-2`} value={f.specialty_other} onChange={(e) => set("specialty_other", e.target.value)} placeholder="Your specialty" />
              )}
              <label className={label}>Languages spoken</label>
              <div className="flex flex-wrap gap-2">
                {meta.languages.map((l) => (
                  <button key={l} type="button" onClick={() => toggleLang(l)}
                    className={`px-3 py-2 rounded-full text-sm font-semibold border ${f.languages.includes(l) ? "bg-[#EF9F27] text-[#111111] border-[#EF9F27]" : "bg-[#1A1A1A] text-[#F8FAFC] border-[#333333]"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="sp-step-licence">
              <h2 className="font-head text-lg font-bold text-[#F8FAFC]">Licence & credentials</h2>
              <label className={label}>Licence / registration number</label>
              <input data-testid="sp-licence-number" className={input} value={f.licence_number} onChange={(e) => set("licence_number", e.target.value)} />
              <label className={label}>Issuing organisation / licensing body</label>
              <input data-testid="sp-licence-body" className={input} value={f.licence_body} onChange={(e) => set("licence_body", e.target.value)} placeholder="e.g. General Medical Council (UK)" />
              <label className={label}>Country of licence issue</label>
              <input data-testid="sp-licence-country" className={input} value={f.licence_issue_country} onChange={(e) => set("licence_issue_country", e.target.value)} />
              <label className={label}>Date of issue</label>
              <input data-testid="sp-licence-issue" className={`${input} [color-scheme:dark]`} type="date" max={today} value={f.licence_issue_date} onChange={(e) => set("licence_issue_date", e.target.value)} />
              <label className="flex items-center gap-2 mt-4 text-sm text-[#F8FAFC]">
                <input data-testid="sp-no-expiry" type="checkbox" checked={f.licence_no_expiry} onChange={(e) => set("licence_no_expiry", e.target.checked)} className="w-5 h-5 accent-[#EF9F27]" />
                This licence does not expire
              </label>
              {!f.licence_no_expiry && (
                <>
                  <label className={label}>Date of expiry</label>
                  <input data-testid="sp-licence-expiry" className={`${input} [color-scheme:dark]`} type="date" value={f.licence_expiry_date} onChange={(e) => set("licence_expiry_date", e.target.value)} />
                  {expiryPast && (
                    <p className="mt-2 text-xs text-[#EFC77A] bg-[#EF9F27]/10 border border-[#EF9F27]/30 rounded-lg p-2 flex gap-2">
                      <AlertTriangle size={14} className="shrink-0 mt-0.5" /> This licence appears to have expired. You can still submit, but verification may be delayed until this is resolved.
                    </p>
                  )}
                </>
              )}
              <label className={label}>Upload licence / certificate (required)</label>
              <input ref={licenceRef} data-testid="sp-licence-file" type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => setLicenceFile(e.target.files?.[0] || null)} />
              <button type="button" onClick={() => licenceRef.current?.click()} className="w-full rounded-xl border border-dashed border-[#333333] bg-[#1A1A1A] px-4 py-3 min-h-[44px] text-left text-[#F8FAFC] flex items-center gap-2">
                {licenceFile ? <Check size={16} className="text-[#22C55E]" /> : <Upload size={16} className="text-[#EF9F27]" />}
                <span className="truncate">{licenceFile ? licenceFile.name : "Choose a file (PDF/JPG/PNG)"}</span>
              </button>
              <label className={label}>Photo ID (optional)</label>
              <input ref={idRef} data-testid="sp-id-file" type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => setIdFile(e.target.files?.[0] || null)} />
              <button type="button" onClick={() => idRef.current?.click()} className="w-full rounded-xl border border-dashed border-[#333333] bg-[#1A1A1A] px-4 py-3 min-h-[44px] text-left text-[#F8FAFC] flex items-center gap-2">
                {idFile ? <Check size={16} className="text-[#22C55E]" /> : <Upload size={16} className="text-[#EF9F27]" />}
                <span className="truncate">{idFile ? idFile.name : "Choose a file (optional)"}</span>
              </button>
              <label className={label}>Credentials summary</label>
              <textarea data-testid="sp-credentials" rows={3} className={input} value={f.credentials_summary} onChange={(e) => set("credentials_summary", e.target.value)} placeholder="Qualifications, years of experience, affiliations" />
            </div>
          )}

          {step === 2 && (
            <div data-testid="sp-step-fees">
              <h2 className="font-head text-lg font-bold text-[#F8FAFC]">Fees & availability</h2>
              <label className={label}>Review fee</label>
              <div className="flex gap-2">
                <input data-testid="sp-fee" className={input} type="number" value={f.review_fee} onChange={(e) => set("review_fee", e.target.value)} placeholder="120" />
                <select data-testid="sp-currency" className={`${input} w-28`} value={f.currency} onChange={(e) => set("currency", e.target.value)}>
                  {["USD", "GBP", "EUR", "GHS", "NGN", "KES", "ZAR"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <label className={label}>Average turnaround (business days)</label>
              <input data-testid="sp-turnaround" className={input} type="number" min={1} value={f.avg_turnaround_days} onChange={(e) => set("avg_turnaround_days", e.target.value)} />
            </div>
          )}

          {step === 3 && (
            <div data-testid="sp-step-review">
              <h2 className="font-head text-lg font-bold text-[#F8FAFC]">Review & submit</h2>
              <div className="mt-3 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-4 text-sm text-[#E5E5E5] space-y-1">
                <p><span className="text-[#A1A1AA]">Name:</span> {f.full_name}</p>
                <p><span className="text-[#A1A1AA]">Specialty:</span> {f.specialty === "Other" ? f.specialty_other : f.specialty}</p>
                <p><span className="text-[#A1A1AA]">Licence:</span> {f.licence_number} · {f.licence_body}</p>
                <p><span className="text-[#A1A1AA]">Expiry:</span> {f.licence_no_expiry ? "No expiry" : f.licence_expiry_date}</p>
                <p><span className="text-[#A1A1AA]">Documents:</span> {licenceFile ? "Licence ✓" : "—"}{idFile ? ", Photo ID ✓" : ""}</p>
              </div>
              <p className="text-xs text-[#A1A1AA] mt-4 leading-relaxed">
                By submitting, your application enters manual review. You'll be notified once verified. You
                won't appear to patients or receive cases until an admin approves your licence.
              </p>
            </div>
          )}

          <div className="mt-8 flex gap-3">
            {step > 0 && (
              <button data-testid="sp-back" onClick={() => setStep(step - 1)} className="flex-1 rounded-full bg-[#222222] border border-[#333333] text-[#F8FAFC] font-bold px-4 py-3 min-h-[48px]">Back</button>
            )}
            {step < 3 ? (
              <button data-testid="sp-next" onClick={() => setStep(step + 1)} disabled={(step === 0 && !step0ok) || (step === 1 && !step1ok)}
                className="flex-1 rounded-full bg-[#EF9F27] text-[#111111] font-bold px-4 py-3 min-h-[48px] disabled:opacity-40">Continue</button>
            ) : (
              <button data-testid="sp-submit" onClick={submit} disabled={submitting}
                className="flex-1 rounded-full bg-[#EF9F27] text-[#111111] font-bold px-4 py-3 min-h-[48px] disabled:opacity-60">
                {submitting ? "Submitting…" : "Submit application"}
              </button>
            )}
          </div>

          <p className="text-center text-sm text-[#A1A1AA] mt-6">
            Already applied?{" "}
            <button data-testid="sp-goto-login" onClick={() => navigate("/specialist/login")} className="font-bold text-[#EF9F27]">Sign in</button>
          </p>
        </div>
      </div>
    </div>
  );
}
